import { Controller } from "@hotwired/stimulus"

// Modal + clipboard wiring for the "Get the embed code" surface on
// widget pages. Renders the iframe snippet, lets the user pick a
// height, and copies the snippet via the Clipboard API.
//
// Mounted by shared/_embed_snippet.html.erb. Each widget page
// passes embed_url / widget_title / default_height as values.
export default class extends Controller {
  static targets = [
    "modal", "dialog", "snippet", "heightSelect", "copyButton", "copyStatus"
  ]
  static values = {
    embedUrl:    String,
    widgetTitle: String,
    height:      Number
  }

  connect() {
    this.renderSnippet()
  }

  // Show the modal. Sets [data-open=true] on the modal wrapper so
  // the Tailwind `data-[open=true]:flex` class kicks in. Also pins
  // body scroll so the host page doesn't scroll while the modal
  // is open.
  open(event) {
    if (event) event.preventDefault()
    this.modalTarget.dataset.open = "true"
    this._previouslyFocused = document.activeElement
    this._previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    // Focus the dialog so Escape works without needing a tab in
    // first, and screen readers announce the modal.
    requestAnimationFrame(() => {
      const focusTarget = this.dialogTarget.querySelector("button, select, [tabindex]")
      if (focusTarget) focusTarget.focus()
    })
  }

  close(event) {
    if (event) event.preventDefault()
    this.modalTarget.dataset.open = "false"
    document.body.style.overflow = this._previousOverflow || ""

    // Return focus to the trigger that opened the modal, for
    // keyboard users.
    if (this._previouslyFocused && this._previouslyFocused.focus) {
      this._previouslyFocused.focus()
    }
  }

  // Modal backdrop click closes; click inside the dialog doesn't.
  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) this.close(event)
  }

  // Update the iframe height when the dropdown changes.
  updateHeight(event) {
    this.heightValue = parseInt(event.target.value, 10) || this.heightValue
    this.renderSnippet()
  }

  renderSnippet() {
    if (!this.hasSnippetTarget) return

    const url    = this.embedUrlValue
    const height = this.heightValue || 600
    const title  = this.widgetTitleValue || "Embedded widget"

    this.snippetTarget.textContent =
      `<iframe src="${url}"\n` +
      `        width="100%" height="${height}" frameborder="0"\n` +
      `        loading="lazy" title="${title}">\n` +
      `</iframe>`
  }

  // Clipboard write with a graceful fallback for older browsers /
  // environments where the modern API isn't available.
  async copy(event) {
    if (event) event.preventDefault()
    const snippet = this.snippetTarget.textContent

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(snippet)
      } else {
        this._legacyCopy(snippet)
      }
      this._flashCopied("Copied!")
    } catch (_e) {
      this._flashCopied("Press Cmd/Ctrl-C to copy", true)
    }
  }

  _legacyCopy(text) {
    const tmp = document.createElement("textarea")
    tmp.value = text
    tmp.setAttribute("readonly", "")
    tmp.style.position = "absolute"
    tmp.style.left = "-9999px"
    document.body.appendChild(tmp)
    tmp.select()
    document.execCommand("copy")
    document.body.removeChild(tmp)
  }

  _flashCopied(message, isFallback = false) {
    if (!this.hasCopyStatusTarget) return
    this.copyStatusTarget.textContent = message
    this.copyStatusTarget.classList.remove("opacity-0")
    this.copyStatusTarget.classList.toggle("text-amber-700", isFallback)
    this.copyStatusTarget.classList.toggle("text-emerald-700", !isFallback)

    clearTimeout(this._flashTimeout)
    this._flashTimeout = setTimeout(() => {
      this.copyStatusTarget.classList.add("opacity-0")
    }, 2200)
  }
}
